import axios from "axios";
import { BACKEND_URL } from "@/config/api";

/**
 * Cliente HTTP da API do Delivery.
 * Os interceptors (token, X-Company-Code e erros) são registrados em
 * axiosInterceptor.js e valem só para esta instância: chamadas a terceiros
 * (ex.: ViaCEP) usam o axios puro e nunca recebem o JWT.
 *
 * Opções extras aceitas no config de cada chamada:
 * - silent: true          → não abre o modal global de erro
 * - silentStatuses: [404] → não abre o modal só para esses status HTTP
 * - skipCompanyScope: true → não envia X-Company-Code (owner global)
 */
const apiClient = axios.create({ baseURL: BACKEND_URL });

/**
 * Desembrulha os 3 formatos de resposta da API:
 * envelope `{ data: [...] }`, JSON direto e primitivo.
 */
export const unwrap = (response) => {
  const body = response?.data;
  if (body && typeof body === "object" && !Array.isArray(body) && "data" in body) {
    return body.data;
  }
  return body;
};

/** Garante uma lista (envelope com data omitido/nulo vira []) */
export const unwrapList = (response) => {
  const payload = unwrap(response);
  if (Array.isArray(payload)) return payload;
  return payload == null || payload === "" ? [] : [payload];
};

/** O envelope traz `data` sempre como lista, mesmo num GET por id */
export const unwrapOne = (response) => {
  const payload = unwrap(response);
  return Array.isArray(payload) ? (payload[0] ?? null) : (payload ?? null);
};

/** Mensagem de erro nos 2 formatos da API (plano e envelope com error.description) */
export const getApiErrorMessage = (error) => {
  const data = error?.response?.data;
  if (!data) return null;
  if (typeof data === "string") return data.trim() || null;
  return (
    data.message ||
    data.error?.description ||
    (typeof data.error === "string" ? data.error : null) ||
    data.msg ||
    null
  );
};

/** Código de erro da API (ex.: TENANT005, ERRC0126) */
export const getApiErrorCode = (error) => {
  const data = error?.response?.data;
  if (!data || typeof data !== "object") return null;
  return data.code || data.error?.code || null;
};

export default apiClient;
